import type { OpaqueCursorPagination } from './authorization.types';
import type { LegalDocumentListItem } from './legal-documents.types';
import type { SystemDisplayTimeZone } from './timezone.utils';

/** Days before a signature's expiry during which it counts as expiring soon. */
export const ORGANIZATION_SIGNATURE_EXPIRING_SOON_DAYS = 30;

/**
 * Why an in-force Organization agreement still needs a PRINCIPAL's signature:
 * nobody signed it, the latest signature expired, or it expires soon.
 */
export type OutstandingSignatureState = 'unsigned' | 'expired' | 'expiring_soon';

export const OUTSTANDING_SIGNATURE_STATES: ReadonlyArray<OutstandingSignatureState> = [
    'unsigned',
    'expired',
    'expiring_soon',
];

export interface OutstandingSignaturesSearchRequest {
    /** Matches Organization names and document titles. */
    terms?: string[];
    limit?: number;
    cursor?: string;
    /** Only these states; every outstanding state when absent. */
    signature_states?: OutstandingSignatureState[];
}

interface OutstandingSignatureBase {
    legal_document_assignment_uuid: string;
    organization: {
        organization_uuid: string;
        organization_name: string;
    };
    legal_document: LegalDocumentListItem;
}

interface OutstandingSignatureLatestSignature {
    accepted_ts: string;
    expires_ts: string;
    signer: { user_uuid: string; primary_email: string };
}

// Discriminator: signature_state
export type OutstandingSignature =
    | (OutstandingSignatureBase & {
          signature_state: 'unsigned';
          latest_signature: null;
      })
    | (OutstandingSignatureBase & {
          signature_state: 'expired';
          latest_signature: OutstandingSignatureLatestSignature;
      })
    | (OutstandingSignatureBase & {
          signature_state: 'expiring_soon';
          latest_signature: OutstandingSignatureLatestSignature;
      });

export interface SearchOutstandingSignaturesPageResponse {
    outstanding_signatures: OutstandingSignature[];
    pagination: OpaqueCursorPagination;
    display_timezone: SystemDisplayTimeZone;
}

/** Narrows a document's acceptances list; every filter is optional. */
export interface LegalDocumentAcceptancesSearchRequest {
    /** Matches the accepting user's email and the Organization name. */
    terms?: string[];
    limit?: number;
    cursor?: string;
    organization_uuid?: string;
    /** Whether the accepted text is the document's current text or an earlier one. */
    accepted_text?: 'current' | 'earlier';
}
